import React, { useEffect, useRef, useState } from 'react';
import { Quote } from 'lucide-react';
import { testimonials } from '../data/mock';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';

const Testimonials = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="py-32 bg-gray-50"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="overflow-hidden">
            <p
              className={`text-gray-400 text-sm uppercase tracking-widest mb-4 transition-all duration-700 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
              }`}
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              What Others Say
            </p>
          </div>
          <div className="overflow-hidden">
            <h2
              className={`text-4xl md:text-5xl font-light text-gray-900 transition-all duration-700 delay-100 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
              }`}
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              Testimonials
            </h2>
          </div>
        </div>

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.id}
              className={`relative bg-white p-8 md:p-10 rounded-2xl shadow-sm hover:shadow-lg transition-all duration-500 hover:-translate-y-1 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-12 opacity-0'
              }`}
              style={{ transitionDelay: `${200 + index * 150}ms` }}
            >
              {/* Quote Icon */}
              <div className="absolute top-6 right-6 opacity-10">
                <Quote size={48} />
              </div>

              {/* Content */}
              <p
                className="text-gray-600 text-lg leading-relaxed mb-8 relative z-10"
                style={{ fontFamily: 'Figtree, sans-serif' }}
              >
                "{testimonial.content}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={testimonial.avatar} alt={testimonial.name} />
                  <AvatarFallback className="bg-gray-200 text-gray-600">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <p
                    className="font-medium text-gray-900"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  >
                    {testimonial.name}
                  </p>
                  <p
                    className="text-sm text-gray-500"
                    style={{ fontFamily: 'Figtree, sans-serif' }}
                  >
                    {testimonial.role}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
